import React from "react";
import { Link, useLocation } from "react-router-dom";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";


// Footer link types
interface FooterLink {
  label: string;
  href: string;
  isRoute: boolean; // true for React Router routes, false for anchors/external
  disabled?: boolean; // true for links not yet connected
}

const footerLinks: {
  product: FooterLink[];
  company: FooterLink[];
  legal: FooterLink[];
} = {
  product: [
    { label: "Why TerraMatrix", href: "/#why", isRoute: false },
    { label: "How It Works", href: "/#how-it-works", isRoute: false },
    { label: "Industries", href: "/#industries", isRoute: false },
  ],
  company: [
    { label: "About", href: "/about", isRoute: true },
    { label: "Contact", href: "/contact", isRoute: true },
  ],
  legal: [
    { label: "Privacy Policy", href: "#", isRoute: false, disabled: true },
    { label: "Terms of Service", href: "#", isRoute: false, disabled: true },
    { label: "Cookie Policy", href: "#", isRoute: false, disabled: true },
  ],
};

export const LandingFooter: React.FC = () => {
  const location = useLocation();
  const isOnLandingPage = location.pathname === "/" || location.pathname === "/landing" || location.pathname === "/landing/v1";

  // Handle anchor link clicks with smooth scrolling (for same-page anchors)
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Only handle same-page anchors on the landing page
    if (isOnLandingPage && href.startsWith("/#")) {
      e.preventDefault();
      const targetId = href.substring(2); // Remove "/#"
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" });
      }
    }
    // For other pages, the default behavior (full navigation to /#section) works correctly
  };

  // Render a footer link
  const renderFooterLink = (link: FooterLink) => {
    if (link.disabled) {
      return (
        <span className="text-gray-500 cursor-default">
          {link.label}
        </span>
      );
    }

    if (link.isRoute) {
      return (
        <Link to={link.href} className="text-gray-300 hover:text-white transition-colors">
          {link.label}
        </Link>
      );
    }

    return (
      <a
        href={link.href}
        onClick={(e) => handleAnchorClick(e, link.href)}
        className="text-gray-300 hover:text-white transition-colors"
      >
        {link.label}
      </a>
    );
  };

  return (
    <footer className="py-16 md:py-20 text-primary-foreground bg-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/">
              <TerraMatrixBrand
                markClassName="h-10 w-10"
                nameClassName="text-3xl text-white"
                className="mb-6"
              />
            </Link>
            <p className="text-gray-400 max-w-sm leading-relaxed mb-6">
              The operating system for real-world infrastructure. Monitor, automate, and optimize
              your operations with AI-powered IoT.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>{renderFooterLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>{renderFooterLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>{renderFooterLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} TerraMatrix. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">Built with precision for real-world operations.</p>
        </div>
      </div>
    </footer>
  );
};
